import { ApiPromise } from '@polkadot/api'
import { Option, StorageKey } from '@polkadot/types'
import { AccountId, AccountId32 } from '@polkadot/types/interfaces'
import { PalletSocietyVote } from '@polkadot/types/lookup'
import { useEffect, useState } from 'react'
import { AccountIdentity } from '../../../../components/AccountIdentity'
import { AccountHeader } from '../../components/AccountHeader'
import { Identicon } from '../../components/Identicon'
import { LoadingSpinner } from '../../components/LoadingSpinner'
import { Offcanvas } from '../../components/Offcanvas'

type VoteType = 'Skeptic' | 'Approve' | 'Reject'
type GroupedVotes = Record<VoteType, AccountId[]>

type Props = {
  api: ApiPromise
  show: boolean
  candidateId: AccountId
  onClose: () => void
}

export function CandidateDetailsOffcanvas({ api, candidateId, show, onClose }: Props) {
  const [votes, setVotes] = useState<GroupedVotes | null>(null)

  useEffect(() => {
    // TODO: cache members and member->candidate map
    api.query.society.members.keys().then(async (memberIds) => {
      const candidateMemberMap = memberIds.map((memberId) => [candidateId, memberId])
      const votesResponse = await api.query.society.votes.multi(candidateMemberMap)
      setVotes(groupVotes(candidateMemberMap, votesResponse))
    })
  }, [])

  return (
    <Offcanvas placement="end" show={show} onClose={onClose} header={<h3>Candidate</h3>}>
      <div className="mb-3">
        <AccountHeader accountId={candidateId} />
      </div>
      {votes ? <CanvasBody votes={votes} /> : <LoadingSpinner />}
    </Offcanvas>
  )
}

function CanvasBody({ votes }: { votes: GroupedVotes }) {
  return (
    <>
      {Object.entries(votes).map(([type, memberIds]) =>
        memberIds.length === 0 ? <></> : <VoterList type={type} memberIds={memberIds} key={type} />
      )}
    </>
  )
}

function VoterList({ type, memberIds }: { type: string; memberIds: AccountId[] }) {
  return (
    <div className="mt-4">
      <h4>{type}s</h4>
      {memberIds.map((id) => (
        <div key={id.toString()} className="mb-2 ms-2">
          <Identicon value={id.toHuman()} size={22} theme="polkadot" className="me-2" />
          <AccountIdentity accountId={id} />
        </div>
      ))}
    </div>
  )
}

function groupVotes(
  candidateMemberMap: (StorageKey<[AccountId32]> | AccountId)[][],
  votesResponse: Option<PalletSocietyVote>[]
): GroupedVotes {
  // TODO: fix me
  const initial = { Approve: [], Reject: [], Skeptic: [] }
  return votesResponse.reduce((grouped, vote, idx) => {
    if (vote.isNone) return grouped

    const type = vote.unwrap().Type

    return {
      ...grouped,
      // Associate voter id to vote based on list position
      [type as unknown as string]: Array.of(candidateMemberMap[idx][1], ...grouped['Approve'])
    }
  }, initial)
}
